import { useMsal } from '@azure/msal-react';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { SocialLoginTypeEnum } from '../../enums/SocialLoginTypeEnum';
import { StorageItemKeyEnum } from '../../enums/StorageItemKeyEnum';
import type { OAuthProvider } from '../../interfaces/UserApiService';
import { appendAiGrantPreferencesToUrl } from '../../lib/aiGrantPreferencesUrl';
import { loginRequest } from '../../lib/msalConfig';
import { FileService } from '../../services/fileApiService';
import { useAppStore } from '../../store/store';
import { getStorageItem, storeStorageItem } from '../../utils/storage';

import { App } from 'antd';
import type { OTPRef } from 'antd/lib/input/OTP';
import EnterFilePasswordForm from '../../components/Forms/EnterFilePasswordForm';
import TotpForm from '../../components/Forms/TotpForm';
import { ApiErrorEnum } from '../../enums/ApiErrorEnum';
import { FilesTableEnum } from '../../enums/FilesTableEnum';
import type FileDetail from '../../interfaces/FileDetail';
import { authService } from '../../services/authService';
import { UserService } from '../../services/userApiService';
import {
  authenticationResponseToAssertionJson,
  isPasskeySupported,
  passkeyAuthenticateWithOptionsString,
} from '../../utils/webauthnClient';
import {
  clearLoginReturnUrl,
  consumeLoginReturnUrl,
  normalizeLoginReturnPath,
  peekLoginReturnUrl,
  persistLoginReturnUrl,
  resolveOAuthReturnUrlToPersist,
} from '../../utils/loginReturnUrl';
import { getApiErrorCode, getApiErrorDetail } from '../../utils/apiErrorCode';
import { validateEmail } from '../../utils/utils';
import { useAuth } from '../useAuth';

const TIMER_DURATION = 30;
const SUBMIT_RATE_LIMIT_WINDOW_MS = 2000; // 2s, the time window to count the submit
const SUBMIT_MAX_ATTEMPTS_IN_WINDOW = 3; // max 3 submit in window
const SUBMIT_BLOCK_DURATION = 30; // 30s block after abuse

const fileService = new FileService();
const userService = new UserService();

interface FormState {
  email?: string;
  code?: string;
}

/** Which second-factor UI to show after OTP when policy allows more than one method. */
type TwoFactorUiMode = 'passkey' | 'pick' | 'totp';

/** Client state for the partial session between OTP verification and second-factor completion. */
interface PartialAuthStepData {
  expiresAt: string;
  partialAuthToken: string;
  passkeyAvailable: boolean;
  totpAvailable: boolean;
  twoFactorUiMode: TwoFactorUiMode;
  userEmail: string;
}

/**
 * Client state for the mandatory TOTP setup flow, triggered by a `403 TOTP_SETUP_REQUIRED`
 * response (user has no TOTP yet and the company's grace period elapsed). Distinct from
 * `PartialAuthStepData`, which is for users who already have a second factor to verify.
 */
interface ForcedTotpSetupStepData {
  expiresAt: string;
  partialAuthToken: string;
  userEmail: string;
}

function toNumericOtpCode(value: string): number {
  return Number.parseInt(value, 10);
}

/**
 * Axios error or httpClient-normalized rejection (`httpClient` sets `response` to `response.data`
 * and puts HTTP status on `status`).
 */
interface AxiosLikeHttpError {
  message?: string;
  name?: string;
  response?: unknown;
  status?: number;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Normalizes error payloads from passwordless/passkey endpoints around HTTP metadata.
 */
function readPasswordlessHttpErrorPayload(error: unknown): {
  apiErrorName?: string;
  axiosMessage?: string;
  detailText?: string;
  httpStatus?: number;
  why?: string;
} {
  const axiosError = error as AxiosLikeHttpError;
  const axiosMessage = typeof axiosError.message === 'string' ? axiosError.message : undefined;
  const wrapped = axiosError.response;

  let httpStatus: number | undefined;
  let rawData: unknown;

  if (
    wrapped &&
    typeof wrapped === 'object' &&
    typeof (wrapped as { status?: unknown }).status === 'number' &&
    'data' in (wrapped as object)
  ) {
    const axiosWrapper = wrapped as { data?: unknown; status: number };
    httpStatus = axiosWrapper.status;
    rawData = axiosWrapper.data;
  } else if (typeof axiosError.status === 'number' && isPlainObject(wrapped)) {
    httpStatus = axiosError.status;
    rawData = wrapped;
  } else {
    return {
      axiosMessage,
      httpStatus: typeof axiosError.status === 'number' ? axiosError.status : undefined,
    };
  }

  if (!isPlainObject(rawData)) {
    return { httpStatus, axiosMessage };
  }

  const apiErrorName = getApiErrorCode(rawData);
  const detailText = getApiErrorDetail(rawData);
  const why = typeof rawData.why === 'string' ? rawData.why : undefined;

  return { httpStatus, apiErrorName, detailText, why, axiosMessage };
}

function isWebAuthnUserCancellationError(error: unknown): boolean {
  const domError = error as { name?: string };
  return domError.name === 'NotAllowedError' || domError.name === 'AbortError';
}

type PasskeyApiErrorTranslationKey =
  | 'errors.passKeyDataRequired'
  | 'errors.passKeyForbidden'
  | 'errors.passKeyLimitReached'
  | 'errors.passKeyLockedOut'
  | 'errors.passKeyNotAllowed'
  | 'errors.passKeyNotEnabled'
  | 'errors.passKeyNotFound'
  | 'errors.passKeySessionExpired'
  | 'errors.passKeyVerificationFailed';

function passkeyApiErrorNameToTranslationKey(
  apiErrorName: string | undefined,
): PasskeyApiErrorTranslationKey | null {
  if (!apiErrorName) {
    return null;
  }
  switch (apiErrorName) {
    case ApiErrorEnum.PasskeyDataRequired:
      return 'errors.passKeyDataRequired';
    case ApiErrorEnum.Forbidden:
      return 'errors.passKeyForbidden';
    case ApiErrorEnum.PasskeyLimitReached:
      return 'errors.passKeyLimitReached';
    case ApiErrorEnum.PasskeyLockedOut:
      return 'errors.passKeyLockedOut';
    case ApiErrorEnum.PasskeyNotAllowed:
      return 'errors.passKeyNotAllowed';
    case ApiErrorEnum.PasskeyNotEnabled:
      return 'errors.passKeyNotEnabled';
    case ApiErrorEnum.PasskeyNotFound:
      return 'errors.passKeyNotFound';
    case ApiErrorEnum.PasskeySessionExpired:
      return 'errors.passKeySessionExpired';
    case ApiErrorEnum.PasskeyVerificationFailed:
      return 'errors.passKeyVerificationFailed';
    default:
      return null;
  }
}

/** Error shape from WebAuthn DOM or Axios during passkey 2FA (options/complete). */
interface PasskeyTwoFactorFlowCaughtError {
  message?: string;
  name?: string;
  response?: {
    data?: { message?: string; name?: string; value?: string; why?: string };
    status?: number;
  };
}

interface PasswordlessHttpFailureDeps {
  notification: {
    error: (args: { title: string }) => void;
    warning: (args: { title: string }) => void;
  };
  setPasswordlessHiddenByPolicy: (hidden: boolean) => void;
  t: TFunction;
}

/** Reads a query param and returns it only when it is a well-formed email address. */
function readValidEmailQueryParam(
  searchParams: URLSearchParams,
  paramName: string,
): string | undefined {
  const rawValue = searchParams.get(paramName)?.trim();
  if (!rawValue || !validateEmail(rawValue)) {
    return undefined;
  }
  return rawValue;
}

/**
 * Resolves the email pre-filled on the login form.
 * `user` query param wins over persisted store values when valid.
 */
export function resolveInitialLoginEmail(
  searchParams: URLSearchParams,
  authenticatedUserEmail?: string | null,
  storedAuthenticatedUserEmail?: string | null,
): string {
  const queryUserEmail = readValidEmailQueryParam(searchParams, 'user');
  if (queryUserEmail) {
    return queryUserEmail;
  }

  if (authenticatedUserEmail && validateEmail(authenticatedUserEmail)) {
    return authenticatedUserEmail;
  }

  if (storedAuthenticatedUserEmail && validateEmail(storedAuthenticatedUserEmail)) {
    return storedAuthenticatedUserEmail;
  }

  return readValidEmailQueryParam(searchParams, 'email') ?? '';
}

function notifyPasswordlessHttpFailure(
  error: unknown,
  { notification, setPasswordlessHiddenByPolicy, t }: PasswordlessHttpFailureDeps,
): void {
  const { apiErrorName, axiosMessage, detailText, httpStatus, why } =
    readPasswordlessHttpErrorPayload(error);
  const combinedLower = `${detailText ?? ''} ${apiErrorName ?? ''}`.toLowerCase();

  if (httpStatus === 400 && apiErrorName === ApiErrorEnum.PasskeyNotEnabled) {
    setPasswordlessHiddenByPolicy(true);
    notification.warning({ title: t('auth.passwordlessNotEnabled') });
    return;
  }

  if (
    httpStatus === 400 &&
    combinedLower.includes('passwordless') &&
    combinedLower.includes('not enabled')
  ) {
    setPasswordlessHiddenByPolicy(true);
    notification.warning({ title: t('auth.passwordlessNotEnabled') });
    return;
  }

  if (
    why === ApiErrorEnum.AccountDisabled ||
    why === ApiErrorEnum.LicenseExpired ||
    why === ApiErrorEnum.AccountExpired
  ) {
    notification.error({ title: detailText ?? t('errors.genericError') });
    return;
  }

  const passkeyTranslationKey =
    passkeyApiErrorNameToTranslationKey(apiErrorName) ?? passkeyApiErrorNameToTranslationKey(why);

  if (passkeyTranslationKey) {
    notification.error({ title: t(passkeyTranslationKey) });
    return;
  }

  notification.error({
    title: detailText ?? axiosMessage ?? t('errors.genericError'),
  });
}

function useLogin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { notification, modal } = App.useApp();
  const authenticatedUserEmail = useAppStore((s) => s.authenticatedUserEmail);
  const { fileId } = useParams<{ fileId: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [a11yLiveMessage, setA11yLiveMessage] = useState<string>('');
  const [isRedirectingToOAuth, setIsRedirectingToOAuth] = useState<boolean>(false);
  const [isDummyFileSet, setIsDummyFileSet] = useState<boolean>(false);
  const [isPageLoaded, setIsPageLoaded] = useState<boolean>(false);
  const [isLoadingOverlayVisible, setIsLoadingOverlayVisible] = useState<boolean>(true);
  const [otpResetKey, setOtpResetKey] = useState(0);
  const storedDirectOAuthProviders = useAppStore((s) => s.directOAuthProviders);
  const setDirectOAuthProviders = useAppStore((s) => s.setDirectOAuthProviders);
  const [aiGrantURL, setAiGrantURL] = useState<string | null>(null);
  const [isMobileApp, setIsMobileApp] = useState<boolean>(
    () => sessionStorage.getItem('mobileApp') === '1',
  );
  const customRefreshToken = useAppStore((s) => s.customTokens?.rToken);

  const { getUserInfo, isLoggedIn, authenticatedUser, redirectLoggedInUser, deviceInfo } =
    useAuth();

  // MSAL hooks for EntraID
  const { instance: msalInstance } = useMsal();
  //const isMsalAuthenticated = useIsAuthenticated();

  // Temporary use of the files hook to open the file viewer, next we will use the useOpenFile hook
  const [form, setForm] = useState<FormState>(() => ({
    code: '',
    email: resolveInitialLoginEmail(searchParams, authenticatedUser?.email, authenticatedUserEmail),
  }));

  const [isOtpRequested, setIsOtpRequested] = useState<boolean>(false);
  const [otpTimer, setOtpTimer] = useState<number | null>(null);
  const [selectedFile, setSelectedFile] = useState<any | null>(null);
  const memoizedRedirectLoggedInUser = useCallback(redirectLoggedInUser, []);
  const otpInputRef = useRef<OTPRef>(null);
  const totpInputRef = useRef<OTPRef>(null);

  /** Set when POST /user/auth/v2 requires 2FA (TOTP and/or passkey). */
  const [totpRequiredData, setTotpRequiredData] = useState<PartialAuthStepData | null>(null);
  /** Set when POST /user/auth/v2 returns 403 TOTP_SETUP_REQUIRED (mandatory setup, no TOTP yet). */
  const [totpSetupRequiredData, setTotpSetupRequiredData] =
    useState<ForcedTotpSetupStepData | null>(null);
  /** Seconds left until the forced-setup partial token expires; mirrors totpRemainingSeconds below. */
  const [totpSetupRemainingSeconds, setTotpSetupRemainingSeconds] = useState<number | null>(null);
  const [passwordlessHiddenByPolicy, setPasswordlessHiddenByPolicy] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  /** When true, show backup code input instead of 6-digit OTP; submit sends isBackupCode: true */
  const [useBackupCodeMode, setUseBackupCodeMode] = useState(false);
  /** Seconds left until temporary TOTP token expires; updated every second when totpRequiredData is set */
  const [totpRemainingSeconds, setTotpRemainingSeconds] = useState<number | null>(null);
  /** When true, Login should open the TOTP setup modal (e.g. after totp/status returns enabled: false) */
  const [autoOpenTotpSetupModal, setAutoOpenTotpSetupModal] = useState(false);

  const submitTimestamps = useRef<number[]>([]);
  const [isSubmitBlocked, setIsSubmitBlocked] = useState(false);

  useEffect(() => {
    if (isLoading || isRedirectingToOAuth) {
      setA11yLiveMessage(t('a11y.loading'));
    } else if (a11yLiveMessage === t('a11y.loading')) {
      setA11yLiveMessage('');
    }
  }, [a11yLiveMessage, isLoading, isRedirectingToOAuth, t]);
  const [submitBlockTimer, setSubmitBlockTimer] = useState(0);

  // Google OAuth state
  const googleOAuthInitialized = useRef<boolean>(false);

  // Direct OAuth state - uses store for persistence, local state for UI reactivity
  const [oauthProviders, setOauthProvidersLocal] = useState<OAuthProvider[] | null>(
    storedDirectOAuthProviders,
  );

  // Sync local state with store changes
  useEffect(() => {
    setOauthProvidersLocal(storedDirectOAuthProviders);
  }, [storedDirectOAuthProviders]);

  // Helper to set both local state and store
  const setOauthProviders = useCallback(
    (providers: OAuthProvider[] | null) => {
      setOauthProvidersLocal(providers);
      setDirectOAuthProviders(providers);
    },
    [setDirectOAuthProviders],
  );

  /**
   * Redirect to OAuth provider - gets state/nonce from backend and redirects
   */
  const redirectToOAuthProvider = async (provider: OAuthProvider): Promise<void> => {
    const redirectUri = `${window.location.origin}/oauth/callback`;

    // Typed identifier (email or login alias). Always bound into signed state as
    // `loginHint`. Provider URL `login_hint` only when the value is an email —
    // an alias would pre-fill the wrong IdP account.
    const loginHint = form.email?.trim() || undefined;
    const loginHintIsEmail = !!loginHint && validateEmail(loginHint);

    try {
      // Step 1: Get state and nonce from backend
      const stateResponse = await userService.getOAuthState({
        tenantUrl: window.location.origin,
        provider: provider.provider,
        clientId: provider.clientId,
        redirectUri: redirectUri,
        ...(loginHint ? { loginHint } : {}),
      });

      const { state, nonce } = stateResponse;

      // Step 2: Save state and context in sessionStorage
      sessionStorage.setItem('oauth_state', state);
      sessionStorage.setItem('oauth_tenant', window.location.origin);
      sessionStorage.setItem('oauth_provider', provider.provider);
      sessionStorage.setItem('oauth_client_id', provider.clientId);

      // Save return URL (file deep link or `?redirect=`) using shared oauth_redirect_url key
      const currentPath = window.location.pathname + window.location.search;
      const returnUrl = resolveOAuthReturnUrlToPersist(currentPath);
      if (returnUrl) {
        persistLoginReturnUrl(returnUrl);
      }

      // Step 3: Build authorization URL with state and nonce from backend.
      // `prompt=select_account` stops the IdP from silently reusing the account
      // still signed in after a logout. `login_hint` only for email identifiers.
      const authParams = new URLSearchParams({
        client_id: provider.clientId,
        response_type: 'code',
        redirect_uri: redirectUri,
        scope: provider.scopes.join(' '),
        state,
        nonce,
        prompt: 'select_account',
      });
      if (loginHintIsEmail && loginHint) {
        authParams.set('login_hint', loginHint);
      }
      const authUrl = `${provider.authorizationEndpoint}?${authParams.toString()}`;

      // Step 4: Redirect to OAuth provider
      window.location.href = authUrl;
    } catch (error: any) {
      if (error.status === 429) {
        notification.error({
          title: t('errors.tooManyRequests'),
        });
        setIsRedirectingToOAuth(false);
        return;
      }

      notification.error({
        title: t('errors.loginFailed'),
      });

      setIsRedirectingToOAuth(false);
      throw error;
    }
  };

  /**
   * Handles the OTP (One Time Password) request process.
   *
   * Prevents multiple OTP requests by checking if a timer is already running.
   * If no timer is active, it requests a new OTP and performs several state update actions:
   * - Sets an OTP request flag to true.
   * - Initializes an OTP timer with a predefined duration.
   * - Stores the OTP timer duration in the local storage.
   * - Resets the 'code' field in the form state.
   */
  const handleRequestOtp = async () => {
    if (otpTimer !== null) return;

    const result = await requestOtp();

    // Only set OTP timer if standard OTP flow was activated (not Direct OAuth)
    if (result === 'otp') {
      setOtpTimer(TIMER_DURATION);
      storeStorageItem('localStorage', StorageItemKeyEnum.otpRequestTimer, TIMER_DURATION);
      setForm((prevForm) => ({
        ...prevForm,
        code: '',
      }));
      setTimeout(() => {
        otpInputRef.current?.focus();
      }, 200);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  /**
   * Asynchronously requests an OTP (One-Time Password) for the user.
   *
   * The function sets the loading state to true, constructs a payload with device and form information,
   * and sends a request for an OTP through the user service. Depending on the response, it either sets
   * the OTP requested state to true or handles errors by creating notifications for specific error statuses.
   *
   * @function requestOtp
   * @async
   */
  const requestOtp = async (): Promise<'otp' | 'directOAuth' | 'error'> => {
    setIsLoading(true);

    try {
      const result = await authService.requestOtp(form.email!, deviceInfo);

      if (
        result.shouldUseDirectOAuth &&
        result.oauthProviders &&
        result.oauthProviders.length > 0
      ) {
        // Direct OAuth flow - save providers to store for persistence
        setOauthProviders(result.oauthProviders);

        const firstProvider = result.oauthProviders[0];
        if (result.oauthProviders.length === 1 && firstProvider) {
          // Single provider - redirect automatically
          setIsRedirectingToOAuth(true);
          await redirectToOAuthProvider(firstProvider);
          return 'directOAuth';
        }
        // Multiple providers - show OAuth buttons for user to choose
        setIsOtpRequested(false);
        return 'directOAuth';
      }

      // Show OTP form
      setIsOtpRequested(true);
      setOauthProviders(null);
      setTimeout(() => {
        otpInputRef.current?.focus();
      }, 200);
      return 'otp';
    } catch (e: any) {
      if (e.response === ApiErrorEnum.UserBlocked) {
        notification.error({
          title: t('errors.authenticationUserBlocked'),
        });
        return 'error';
      }

      if (e.response === ApiErrorEnum.UserExpired) {
        notification.error({
          title: t('errors.authenticationUserExpired'),
        });
        return 'error';
      }

      if (e?.response?.name === ApiErrorEnum.TenantNotFound) {
        notification.error({
          title: e.response.value,
        });
        return 'error';
      }

      if (
        e?.response?.name === ApiErrorEnum.LicenseGlobalExpired ||
        e?.response?.name === ApiErrorEnum.LicenseExpired ||
        e?.response?.name === ApiErrorEnum.LicenseCompanyExpired
      ) {
        notification.error({
          title: t('errors.licenseExpired'),
        });
        return 'error';
      }

      if (e?.response?.name === ApiErrorEnum.IpRegionBlocked) {
        notification.error({
          title: t('errors.ipRegionBlocked.loginTitle'),
          description: e.response.value || t('errors.ipRegionBlocked.loginMessage'),
        });
        return 'error';
      }

      if (e?.response?.name === ApiErrorEnum.EmailFormatNotCorrect) {
        notification.error({
          title: t('errors.invalidEmailOrUsername'),
        });
        return 'error';
      }

      if (e.status === 404) {
        notification.error({
          title: t('errors.emailNotFound', {
            email: form.email,
          }),
        });
        return 'error';
      }

      if (e.status === 429) {
        notification.error({
          title: t('errors.otpRequestLimitExceeded'),
        });
        return 'error';
      }

      notification.error({
        title: t('errors.genericError'),
      });
      return 'error';
    } finally {
      setIsLoading(false);
    }
  };

  /** Clears OTP, 2FA, forced-setup, and temporary partial-auth state so the user can restart from email. */
  const resetToEmailOnly = useCallback(() => {
    setForm((prev) => ({ email: prev.email, code: '' }));
    setTotpRequiredData(null);
    setTotpSetupRequiredData(null);
    setTotpCode('');
    setUseBackupCodeMode(false);
    setIsOtpRequested(false);
    setAutoOpenTotpSetupModal(false);
    useAppStore.getState().clearTemporaryTotpToken();
    useAppStore.getState().setOnTemporaryTotpExpired(null);
  }, []);

  /** Drops the in-progress second-factor/forced-setup step without clearing the email field (e.g. after successful alternate path). */
  const clearTotpStep = useCallback(() => {
    setTotpRequiredData(null);
    setTotpSetupRequiredData(null);
    setTotpCode('');
    setUseBackupCodeMode(false);
    setIsOtpRequested(false);
    setForm((prev) => ({ ...prev, code: '' }));
    useAppStore.getState().clearTemporaryTotpToken();
    useAppStore.getState().setOnTemporaryTotpExpired(null);
  }, []);

  /** Loads the authenticated profile and applies post-login navigation (home, mobile success, return URL, or AI grant). */
  const finalizePostLoginSteps = useCallback(async (): Promise<void> => {
    const shouldRedirectToMobile = isMobileApp;
    const returnUrl =
      peekLoginReturnUrl() ?? normalizeLoginReturnPath(searchParams.get('redirect'));
    if (selectedFile?.fileProtected?.fileId) {
      await getUserInfo({ redirectHome: false });
    } else {
      await getUserInfo({
        redirectHome: !shouldRedirectToMobile && !returnUrl,
        saveAuthenticatedUserEmail: true,
      });
    }
    if (shouldRedirectToMobile) {
      clearLoginReturnUrl();
      navigate('/mobile/auth-success');
      return;
    }
    if (returnUrl) {
      consumeLoginReturnUrl();
      navigate(returnUrl, { replace: true });
      return;
    }
    if (aiGrantURL && customRefreshToken) {
      const { locale, colorSchemePreference, highContrastEnabled, reduceMotionEnabled } =
        useAppStore.getState();
      window.location.href = appendAiGrantPreferencesToUrl(aiGrantURL, {
        locale,
        colorSchemePreference,
        highContrastEnabled,
        reduceMotionEnabled,
        refreshToken: customRefreshToken,
      });
    }
  }, [
    aiGrantURL,
    customRefreshToken,
    getUserInfo,
    isMobileApp,
    navigate,
    searchParams,
    selectedFile?.fileProtected?.fileId,
  ]);

  /**
   * Completes login with WebAuthn using the partial auth token from `authenticateWithOtp`.
   * Surfaces cancellation, expiry, and API errors without throwing.
   */
  const runPasskeyTwoFactorFlow = useCallback(
    async (partialAuthStep: PartialAuthStepData) => {
      setIsLoading(true);
      try {
        const passkeyAuthOptionsResponse = await userService.passkeyAuthOptions({
          partialAuthToken: partialAuthStep.partialAuthToken,
        });
        const webAuthnAssertion = await passkeyAuthenticateWithOptionsString(
          passkeyAuthOptionsResponse.options,
        );
        const assertionJsonPayload = authenticationResponseToAssertionJson(webAuthnAssertion);
        await authService.completeLoginWithPasskeyAssertion(
          partialAuthStep.partialAuthToken,
          passkeyAuthOptionsResponse.sessionId,
          assertionJsonPayload,
        );
        useAppStore.getState().clearTemporaryTotpToken();
        useAppStore.getState().setOnTemporaryTotpExpired(null);
        await finalizePostLoginSteps();
      } catch (caughtError: unknown) {
        const e = caughtError as { name?: string; status?: number };
        if (e.status === 429) {
          notification.error({
            title: t('errors.tooManyRequests'),
          });
          return;
        }

        const passkeyFlowError = caughtError as PasskeyTwoFactorFlowCaughtError;
        if (passkeyFlowError.name === 'NotAllowedError' || passkeyFlowError.name === 'AbortError') {
          notification.info({ title: t('auth.passkeyCancelled') });
        } else {
          const parsedHttpError = readPasswordlessHttpErrorPayload(caughtError);
          if (
            parsedHttpError.why === ApiErrorEnum.InvalidToken ||
            parsedHttpError.apiErrorName === ApiErrorEnum.InvalidToken
          ) {
            notification.error({ title: t('errors.temporaryTotpExpired') });
            resetToEmailOnly();
            return;
          }
          const passkeyTranslationKey =
            passkeyApiErrorNameToTranslationKey(parsedHttpError.apiErrorName) ??
            passkeyApiErrorNameToTranslationKey(parsedHttpError.why);
          if (passkeyTranslationKey) {
            notification.error({ title: t(passkeyTranslationKey) });
            return;
          }
          notification.error({
            title:
              parsedHttpError.detailText ??
              parsedHttpError.axiosMessage ??
              t('errors.genericError'),
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [finalizePostLoginSteps, notification, resetToEmailOnly, t],
  );

  /**
   * Authenticates the user by sending a login request with the provided payload.
   *
   * This function will set the loading state to true, build a payload with the
   * code, email, and tenant URL, and then attempt to log in the user using the
   * userService. If the login is successful, the login function will be called
   * with the response and a flag set to true. If there is an error (e.g., the OTP
   * has expired), a notification will be created with a danger status. Regardless
   * of success or failure, the loading state will be set to false at the end.
   *
   * If aiGrantURL is set, redirects to that URL with the refreshToken after successful login.
   *
   * @function
   * @async
   * @returns {Promise<void>} A promise that resolves when the authentication process is complete.
   */
  const authenticate = async (code: string): Promise<void> => {
    setIsLoading(true);

    try {
      const result = await authService.authenticateWithOtp(form.email!, toNumericOtpCode(code));

      if (result.kind === 'totpRequired') {
        const totpAvailable = result.totpAvailable;
        const passkeyAvailable = result.passkeyAvailable;
        let twoFactorUiMode: TwoFactorUiMode;
        if (totpAvailable && passkeyAvailable) {
          twoFactorUiMode = 'pick';
        } else if (passkeyAvailable) {
          twoFactorUiMode = 'passkey';
        } else {
          twoFactorUiMode = 'totp';
        }

        const next: PartialAuthStepData = {
          expiresAt: result.expiresAt,
          partialAuthToken: result.partialAuthToken,
          passkeyAvailable,
          totpAvailable,
          twoFactorUiMode,
          userEmail: result.userEmail,
        };

        setTotpRequiredData(next);
        useAppStore.getState().setTemporaryTotpToken({
          expiresAt: result.expiresAt,
          token: result.partialAuthToken,
        });
        setTotpCode('');
        setIsLoading(false);

        if (totpAvailable) {
          try {
            const status = await userService.totpStatus();
            if (status.enabled === false) {
              setAutoOpenTotpSetupModal(true);
            }
          } catch (error: any) {
            if (error.status === 429) {
              notification.error({
                title: t('errors.tooManyRequests'),
              });
            }
            // User can still open "Configure 2FA" manually
          }
        }

        if (twoFactorUiMode === 'passkey') {
          await runPasskeyTwoFactorFlow(next);
        }
        return;
      }

      if (result.kind === 'totpSetupRequired') {
        setTotpSetupRequiredData({
          expiresAt: result.expiresAt,
          partialAuthToken: result.partialAuthToken,
          userEmail: result.userEmail,
        });
        useAppStore.getState().setTemporaryTotpToken({
          expiresAt: result.expiresAt,
          token: result.partialAuthToken,
        });
        setIsLoading(false);
        return;
      }

      await finalizePostLoginSteps();
    } catch (error: any) {
      if (error.status === 429) {
        notification.error({
          title: t('errors.tooManyRequests'),
        });
        return;
      }

      if (error?.response?.name === ApiErrorEnum.OtpWrong) {
        const message = t('errors.otpWrong');
        setA11yLiveMessage(message);
        notification.error({
          title: message,
        });
      } else {
        const message = t('errors.otpExpired');
        setA11yLiveMessage(message);
        notification.error({
          title: message,
        });
      }

      setForm((prevForm) => ({
        ...prevForm,
        code: '',
      }));

      setTimeout(() => {
        otpInputRef.current?.focus();
        setOtpResetKey(otpResetKey + 1);
      }, 200);
    } finally {
      setIsLoading(false);
    }
  };

  /** Complete login with TOTP or backup code after partial auth. */
  const authenticateWithTotp = useCallback(
    async (code: string, isBackupCode: boolean): Promise<void> => {
      if (!totpRequiredData) return;
      setIsLoading(true);
      try {
        await authService.completeLoginWithTotp(
          totpRequiredData.partialAuthToken,
          code,
          isBackupCode,
        );

        useAppStore.getState().clearTemporaryTotpToken();
        useAppStore.getState().setOnTemporaryTotpExpired(null);
        await finalizePostLoginSteps();
      } catch (error: any) {
        if (error.status === 429) {
          notification.error({
            title: t('errors.tooManyRequests'),
          });
          setTotpCode('');
          return;
        }

        notification.error({
          title: t('errors.totpWrong', 'Invalid 2FA code'),
        });
        setTotpCode('');
      } finally {
        setIsLoading(false);
      }
    },
    [totpRequiredData, finalizePostLoginSteps, notification, t],
  );

  /** Switches the 2FA UI from method picker to TOTP entry. */
  const chooseTwoFactorTotp = useCallback(() => {
    setTotpRequiredData((prev) =>
      prev ? { ...prev, twoFactorUiMode: 'totp' as TwoFactorUiMode } : null,
    );
  }, []);

  /** Starts the passkey assertion flow from the method picker. */
  const chooseTwoFactorPasskey = useCallback(() => {
    if (!totpRequiredData) return;
    void runPasskeyTwoFactorFlow(totpRequiredData);
  }, [totpRequiredData, runPasskeyTwoFactorFlow]);

  /**
   * Passwordless path: discovery/options, browser WebAuthn, then token exchange (no email OTP).
   * Hides the CTA when the backend reports passwordless is disabled for the tenant.
   * HTTP failures (options/complete) are separated from WebAuthn DOM errors (user cancel, etc.).
   */
  const handlePasswordlessLogin = useCallback(async () => {
    if (!isPasskeySupported()) {
      notification.warning({ title: t('auth.passkeyNotSupported') });
      return;
    }

    const passwordlessFailureDeps: PasswordlessHttpFailureDeps = {
      notification,
      setPasswordlessHiddenByPolicy,
      t,
    };

    setIsLoading(true);
    try {
      let passwordlessOptionsResponse;
      try {
        passwordlessOptionsResponse = await userService.passkeyPasswordlessOptions({
          tenantUrl: window.location.origin,
          ...(form.email?.trim() ? { username: form.email.trim() } : {}),
        });
      } catch (passwordlessOptionsError: unknown) {
        const e = passwordlessOptionsError as { status?: number };
        if (e.status === 429) {
          notification.error({
            title: t('errors.tooManyRequests'),
          });
          return;
        }
        notifyPasswordlessHttpFailure(passwordlessOptionsError, passwordlessFailureDeps);
        return;
      }

      let assertion;
      try {
        assertion = await passkeyAuthenticateWithOptionsString(passwordlessOptionsResponse.options);
      } catch (webAuthnError: unknown) {
        if (isWebAuthnUserCancellationError(webAuthnError)) {
          notification.info({ title: t('auth.passkeyCancelled') });
          return;
        }
        notification.error({
          title: webAuthnError instanceof Error ? webAuthnError.message : t('errors.genericError'),
        });
        return;
      }

      try {
        await authService.completePasswordlessLogin(
          passwordlessOptionsResponse.sessionId,
          authenticationResponseToAssertionJson(assertion),
        );
      } catch (passwordlessCompleteError: unknown) {
        const e = passwordlessCompleteError as { status?: number };
        if (e.status === 429) {
          notification.error({
            title: t('errors.tooManyRequests'),
          });
          return;
        }

        const parsedCompleteError = readPasswordlessHttpErrorPayload(passwordlessCompleteError);
        const apiErrorCode =
          parsedCompleteError.apiErrorName ?? parsedCompleteError.why ?? undefined;

        // Company / tenant policy: passkeys or passwordless not available (hide CTA like options flow).
        if (apiErrorCode === ApiErrorEnum.PasskeyNotEnabled) {
          setPasswordlessHiddenByPolicy(true);
          notification.warning({ title: t('auth.passwordlessNotEnabled') });
          return;
        }

        // Structured passkey API codes from `ApiErrorEnum` (complete endpoint).
        const passkeyCompleteMessageKey = passkeyApiErrorNameToTranslationKey(apiErrorCode);
        if (passkeyCompleteMessageKey) {
          notification.error({ title: t(passkeyCompleteMessageKey) });
          return;
        }

        notifyPasswordlessHttpFailure(passwordlessCompleteError, passwordlessFailureDeps);
        return;
      }

      await finalizePostLoginSteps();
    } catch (unexpectedError: unknown) {
      const e = unexpectedError as { message?: string; status?: number };
      if (e.status === 429) {
        notification.error({
          title: t('errors.tooManyRequests'),
        });
        return;
      }

      notification.error({
        title:
          unexpectedError instanceof Error ? unexpectedError.message : t('errors.genericError'),
      });
    } finally {
      setIsLoading(false);
    }
  }, [finalizePostLoginSteps, form.email, notification, setPasswordlessHiddenByPolicy, t]);

  const clearAutoOpenTotpSetupModal = useCallback(() => setAutoOpenTotpSetupModal(false), []);

  /** On TOTP (6-digit) input change: update state and auto-submit when 6 digits. Not used in backup code mode. */
  const handleTotpCodeChange = useCallback(
    (value: string) => {
      setTotpCode(value);
      if (value.length === 6) {
        authenticateWithTotp(value, false);
      }
    },
    [authenticateWithTotp],
  );

  /** Toggle between OTP (6-digit) and backup code input; clears current code. */
  const toggleTotpInputMode = useCallback(() => {
    setUseBackupCodeMode((prev) => !prev);
    setTotpCode('');
  }, []);

  /** Open TOTP setup modal (from login: setup-only, no disable 2FA). */
  const setOnTemporaryTotpExpired = useAppStore((s) => s.setOnTemporaryTotpExpired);
  const handleOpenTotpSetup = useCallback(() => {
    const modalInstance = modal.confirm({
      title: t('modal.totpForm.title'),
      content: (
        <TotpForm
          isSetupOnly
          onClose={() => {
            setOnTemporaryTotpExpired(null);
            modalInstance.destroy();
            resetToEmailOnly();
          }}
          onSuccess={() => {
            setOnTemporaryTotpExpired(null);
            modalInstance.destroy();
            resetToEmailOnly();
          }}
          onTemporaryTotpExpired={() => {
            modalInstance.destroy();
            resetToEmailOnly();
            notification.error({ title: t('errors.temporaryTotpExpired') });
            setOnTemporaryTotpExpired(null);
          }}
        />
      ),
      footer: null,
      width: 560,
    });
    setOnTemporaryTotpExpired(() => {
      modalInstance.destroy();
      resetToEmailOnly();
      notification.error({ title: t('errors.temporaryTotpExpired') });
      setOnTemporaryTotpExpired(null);
    });
  }, [modal, notification, t, resetToEmailOnly, setOnTemporaryTotpExpired]);

  /** When backend says 2FA is not set up, open the TOTP setup modal automatically. */
  useEffect(() => {
    if (autoOpenTotpSetupModal) {
      handleOpenTotpSetup();
      clearAutoOpenTotpSetupModal();
    }
  }, [autoOpenTotpSetupModal, handleOpenTotpSetup, clearAutoOpenTotpSetupModal]);

  /** Focus TOTP input when the TOTP 2FA step is shown (same UX as email OTP field). */
  useEffect(() => {
    if (!totpRequiredData || totpRequiredData.twoFactorUiMode !== 'totp') return;
    const id = setTimeout(() => {
      totpInputRef.current?.focus();
    }, 200);
    return () => clearTimeout(id);
  }, [totpRequiredData]);

  /** Countdown from totpRequiredData.expiresAt; at 0 invokes expired callback and resets */
  useEffect(() => {
    if (!totpRequiredData?.expiresAt) {
      setTotpRemainingSeconds(null);
      return;
    }
    const expiresAtMs = new Date(totpRequiredData.expiresAt).getTime();
    let intervalId: ReturnType<typeof setInterval>;
    const update = () => {
      const remaining = Math.max(0, Math.floor((expiresAtMs - Date.now()) / 1000));
      setTotpRemainingSeconds(remaining);
      if (remaining <= 0) {
        clearInterval(intervalId);
        const store = useAppStore.getState();
        if (store.onTemporaryTotpExpired) {
          store.onTemporaryTotpExpired();
        } else {
          setTotpRequiredData(null);
          setTotpCode('');
          setIsOtpRequested(false);
          setForm((prev) => ({ email: prev.email, code: '' }));
          notification.error({ title: t('errors.temporaryTotpExpired') });
        }
        store.clearTemporaryTotpToken();
        store.setOnTemporaryTotpExpired(null);
      }
    };
    update();
    intervalId = setInterval(update, 1000);
    return () => clearInterval(intervalId);
  }, [totpRequiredData?.expiresAt, notification, t]);

  /** Called when the forced TOTP setup (inline TotpForm) completes: finish login instead of resetting to email. */
  const handleForcedTotpSetupSuccess = useCallback(() => {
    useAppStore.getState().clearTemporaryTotpToken();
    setTotpSetupRequiredData(null);
    void finalizePostLoginSteps();
  }, [finalizePostLoginSteps]);

  /** Called when the partial token backing the forced setup flow expires or is rejected (INVALID_TOKEN). */
  const handleForcedTotpSetupExpired = useCallback(() => {
    notification.error({ title: t('errors.temporaryTotpExpired') });
    resetToEmailOnly();
  }, [notification, t, resetToEmailOnly]);

  /** Countdown from totpSetupRequiredData.expiresAt; at 0 resets to email-only login (partial token expired). */
  useEffect(() => {
    if (!totpSetupRequiredData?.expiresAt) {
      setTotpSetupRemainingSeconds(null);
      return;
    }
    const expiresAtMs = new Date(totpSetupRequiredData.expiresAt).getTime();
    let intervalId: ReturnType<typeof setInterval>;
    const update = () => {
      const remaining = Math.max(0, Math.floor((expiresAtMs - Date.now()) / 1000));
      setTotpSetupRemainingSeconds(remaining);
      if (remaining <= 0) {
        clearInterval(intervalId);
        handleForcedTotpSetupExpired();
      }
    };
    update();
    intervalId = setInterval(update, 1000);
    return () => clearInterval(intervalId);
  }, [totpSetupRequiredData?.expiresAt, handleForcedTotpSetupExpired]);

  /**
   * Redirects the user based on the properties of the provided file.
   *
   * @param fileInfo
   * @param fileDetail
   * @param userId
   */
  const checkFileRedirect = (file: FileDetail, userId: string | number | undefined | null) => {
    if (file.isFolder) {
      if (userId && userId.toString() === file.ownerId?.toString()) {
        navigate(`/files/${FilesTableEnum.Personal}/folder/${file.uid}`);
      } else {
        navigate(`/files/${FilesTableEnum.Received}/folder/${file.uid}`);
      }
      return;
    } else {
      navigate(`/viewer/${file.uid}`);
    }
  };

  /**
   * Retrieves information about a specified file.
   *
   * This asynchronous function utilizes the `fileService` to fetch file information based on a specified `fileId`.
   * It manages loading state, handles potential errors, and provides notifications for the user.
   *
   * @function
   * @name getFileDetail
   *
   * @property {string} fileId - The unique identifier of the file whose information is being retrieved.
   *
   * @returns {Promise<void>} A promise that resolves when the file information retrieval process is complete.
   */
  const getFileDetail = useCallback(
    async (requestedFileId: string | number) => {
      setIsLoading(true);
      setIsLoadingOverlayVisible(true);

      fileService
        .getFile(requestedFileId)
        .then((file) => {
          if (file) {
            checkFileRedirect(file, authenticatedUser?.id);
          }
        })
        .catch((e) => {
          if (e.status === 429) {
            notification.error({
              title: t('errors.tooManyRequests'),
            });
            return;
          }

          switch (e.response?.name) {
            case ApiErrorEnum.FolderPasswordRequired: {
              const folderId = e.response?.value?.folderId ?? Number(requestedFileId);
              if (!Number.isFinite(folderId)) return;
              setIsLoadingOverlayVisible(false);
              const passwordModalInstance = modal.confirm({
                title: t('modal.filePasswordForm.title'),
                width: 480,
                content: (
                  <EnterFilePasswordForm
                    fileId={folderId}
                    isOwner={false}
                    onSubmit={() => {
                      passwordModalInstance.destroy();
                      getFileDetail(requestedFileId);
                    }}
                    onCancel={() => passwordModalInstance.destroy()}
                  />
                ),
                footer: null,
              });
              break;
            }
            default:
              notification.error({
                title: t('errors.requestFilePermissions'),
              });
              if (authenticatedUser?.id) {
                redirectLoggedInUser();
              } else {
                navigate('/login');
              }
          }
          return;
        })
        .finally(() => {
          setIsLoading(false);
        });
      // try {
      //   const file = await fileService.getFile(requestedFileId);
      //   if (file) {
      //     checkFileRedirect(file, authenticatedUser?.id);
      //   }
      // } catch (e) {
      //   return;
      //   notification.error({
      //     title: t('errors.requestFilePermissions'),
      //   });
      //   if (authenticatedUser?.id) {
      //     redirectLoggedInUser();
      //   } else {
      //     navigate('/login');
      //   }
      // } finally {
      //   setIsLoading(false);
      // }
    },
    [
      fileService,
      t,
      modal,
      checkFileRedirect,
      authenticatedUser?.id,
      navigate,
      notification,
      redirectLoggedInUser,
    ],
  );

  /**
   * Handles the form submission event.
   * Prevents the default form submission behavior, and based on the state of OTP request,
   * either initiates the request for OTP or proceeds with authentication.
   *
   * @param {FormEvent} e - The form event object.
   * @returns {Promise<void>} - A promise that resolves when the form handling logic is complete.
   */
  const onSubmit = async (): Promise<void> => {
    if (isSubmitBlocked) return;

    // Mandatory TOTP setup has its own inline form (TotpForm) with its own submit handling.
    if (totpSetupRequiredData) return;

    if (totpRequiredData) {
      if (totpRequiredData.twoFactorUiMode === 'pick') {
        return;
      }
      await authenticateWithTotp(totpCode, useBackupCodeMode);
      return;
    }

    const now = Date.now();
    submitTimestamps.current = submitTimestamps.current
      .filter((ts) => now - ts < SUBMIT_RATE_LIMIT_WINDOW_MS)
      .concat(now);

    if (submitTimestamps.current.length >= SUBMIT_MAX_ATTEMPTS_IN_WINDOW) {
      setIsSubmitBlocked(true);
      setSubmitBlockTimer(SUBMIT_BLOCK_DURATION);
      notification.error({
        title: t('auth.loginPageTooManyAttempts') + ` ${SUBMIT_BLOCK_DURATION}s`,
      });
      return;
    }

    if (!isOtpRequested) {
      setIsLoading(true);
      await requestOtp();
    } else {
      await authenticate(form.code || '');
    }
  };

  /**
   * True when the login identifier field is non-empty (email or login alias).
   * Format validation is server-side (`EMAILFORMATNOTCORRECT`).
   */
  const isEmailValid = useMemo(() => !!form?.email?.trim(), [form.email]);

  /**
   * A memoized boolean value that represents whether the login providers are enabled.
   *
   * This variable uses the `loginProviders` state to check if any of the providers are enabled.
   * If any of the providers are enabled, it returns true. Otherwise, it returns false.
   *
   */

  useEffect(() => {
    setIsOtpRequested(false);
    setOauthProviders(null);
    setPasswordlessHiddenByPolicy(false);
  }, [form.email]);

  useEffect(() => {
    if (otpTimer === null) return;

    const intervalId = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev === null || prev === 0) {
          storeStorageItem('localStorage', StorageItemKeyEnum.otpRequestTimer, 0);
          return null;
        } else {
          storeStorageItem('localStorage', StorageItemKeyEnum.otpRequestTimer, prev - 1);
          return prev - 1;
        }
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [otpTimer]);

  useEffect(() => {
    const checkStoredTimer = async () => {
      const storedTimer = await getStorageItem('localStorage', StorageItemKeyEnum.otpRequestTimer);
      if (storedTimer && storedTimer > 0) {
        setOtpTimer(storedTimer);
      }
    };
    checkStoredTimer().then(() => {});
  }, []);

  /**
   * Persist `?redirect=` into the shared oauth_redirect_url key (survives OAuth IdP round-trips).
   * Clear stale return URLs when visiting login without a redirect intent.
   * Must run before the already-logged-in redirect effect below.
   */
  useEffect(() => {
    const fromQuery = normalizeLoginReturnPath(searchParams.get('redirect'));
    if (fromQuery) {
      persistLoginReturnUrl(fromQuery);
    } else if (!fileId) {
      // Keep file deep-link OAuth returns intact; only clear on bare /login.
      clearLoginReturnUrl();
    }
  }, [searchParams, fileId]);

  useEffect(() => {
    if (fileId && !selectedFile && !isDummyFileSet) {
      setSelectedFile({
        fileProtected: {
          fileId: fileId,
          fileExtension: '',
          originaleFileName: 'File Shared',
          userId: null,
        },
      });
      setIsDummyFileSet(true);
    }

    if (isLoggedIn && authenticatedUser && !fileId) {
      const returnUrl =
        consumeLoginReturnUrl() ?? normalizeLoginReturnPath(searchParams.get('redirect'));
      if (returnUrl) {
        navigate(returnUrl, { replace: true });
      } else {
        redirectLoggedInUser();
      }
    }

    // Redirect if user is already logged and there's no fileId
  }, [
    fileId,
    selectedFile,
    isLoggedIn,
    authenticatedUser,
    memoizedRedirectLoggedInUser,
    isDummyFileSet,
    navigate,
    redirectLoggedInUser,
    searchParams,
  ]);

  useEffect(() => {
    if (isLoggedIn && selectedFile && selectedFile.fileProtected?.fileId) {
      getFileDetail(selectedFile.fileProtected.fileId).then(() => {});
    } else {
      setIsLoadingOverlayVisible(false);
    }
  }, [isLoggedIn, selectedFile, memoizedRedirectLoggedInUser]);

  /**
   * Handle redirect to aiGrantURL after successful login
   */
  useEffect(() => {
    if (isLoggedIn && aiGrantURL) {
      if (!customRefreshToken) return;

      sessionStorage.removeItem('aiGrantURL');

      const { locale, colorSchemePreference, highContrastEnabled, reduceMotionEnabled } =
        useAppStore.getState();
      window.location.href = appendAiGrantPreferencesToUrl(aiGrantURL, {
        locale,
        colorSchemePreference,
        highContrastEnabled,
        reduceMotionEnabled,
        refreshToken: customRefreshToken,
      });
    }
  }, [isLoggedIn, aiGrantURL, customRefreshToken]);

  useEffect(() => {
    setIsPageLoaded(true);
  }, []);

  // Initialize authentication on component mount
  useEffect(() => {
    authService.initialize().catch((error) => {
      console.error('Failed to initialize auth service:', error);
    });
  }, []);

  /**
   * Handles the Submit block timer.
   *
   * This effect manages the OTP block timer, which is used to prevent multiple OTP requests within a short period of time.
   * It sets up an interval to decrement the timer and resets the block state when the timer reaches 0.
   *
   * @function
   */
  useEffect(() => {
    if (isSubmitBlocked && submitBlockTimer > 0) {
      const interval = setInterval(() => {
        setSubmitBlockTimer((prev) => {
          if (prev <= 1) {
            setIsSubmitBlocked(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isSubmitBlocked, submitBlockTimer]);

  // Providers are now automatically loaded from Zustand store

  /**
   * Check for aiGrant query parameter and call getAIGrantURL
   *
   * This effect checks if the aiGrant query parameter is set to 'true' or '1'.
   * If so, it calls the getAIGrantURL API with the tenant URL and saves the URL for later redirect.
   * The URL is also saved in sessionStorage to persist across OAuth redirects.
   */
  useEffect(() => {
    const aiGrant = searchParams.get('aiGrant');

    // Check if we have a stored aiGrantURL from before OAuth redirect
    const storedAiGrantURL = sessionStorage.getItem('aiGrantURL');
    if (storedAiGrantURL && !aiGrantURL) {
      setAiGrantURL(storedAiGrantURL);
    }

    if (aiGrant === '1' || aiGrant === 'true') {
      const payload = {
        tenantUrl: window.location.origin,
      };

      userService
        .getAIGrantURL(payload)
        .then((response) => {
          if (response.aiGrantURL) {
            setAiGrantURL(response.aiGrantURL);
            sessionStorage.setItem('aiGrantURL', response.aiGrantURL);

            // If user is already logged in, trigger immediate redirect
            if (isLoggedIn && customRefreshToken) {
              sessionStorage.removeItem('aiGrantURL');
              const { locale, colorSchemePreference, highContrastEnabled, reduceMotionEnabled } =
                useAppStore.getState();
              window.location.href = appendAiGrantPreferencesToUrl(response.aiGrantURL, {
                locale,
                colorSchemePreference,
                highContrastEnabled,
                reduceMotionEnabled,
                refreshToken: customRefreshToken,
              });
            }
          }
        })
        .catch((error) => {
          if ((error as { status?: number }).status === 429) {
            notification.error({
              title: t('errors.tooManyRequests'),
            });
            return;
          }

          console.error('Error getting AIGrant URL:', error);
          notification.error({
            title: t('errors.genericError'),
          });
        });
    }
  }, [searchParams, notification, t, aiGrantURL, isLoggedIn, customRefreshToken]);

  /**
   * Detect mobileApp query parameter for mobile app login flow.
   * When ?mobileApp=1 is present, after login the user is redirected to
   * /mobile/auth-success which passes tokens back to the native app via deep link.
   */
  useEffect(() => {
    const mobileApp = searchParams.get('mobileApp');
    if (mobileApp === '1' || mobileApp === 'true') {
      setIsMobileApp(true);
      sessionStorage.setItem('mobileApp', '1');
    }
  }, [searchParams]);

  /**
   * Redirect to mobile auth success page when user is logged in and came from mobile app.
   */
  useEffect(() => {
    if (isLoggedIn && isMobileApp) {
      navigate('/mobile/auth-success');
    }
  }, [isLoggedIn, isMobileApp, navigate]);

  /**
   * Initialize Google OAuth
   */
  useEffect(() => {
    if (googleOAuthInitialized.current) return;
    if (!import.meta.env.VITE_APP_GOOGLE_CLIENT_ID) return;

    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google?.accounts?.id) {
        googleOAuthInitialized.current = true;
      }
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, []);

  /**
   * Handle Direct OAuth login - redirect to OAuth provider (used by button click)
   */
  const handleDirectOAuthLogin = useCallback(
    async (provider: OAuthProvider): Promise<void> => {
      try {
        setIsRedirectingToOAuth(true);
        await redirectToOAuthProvider(provider);
      } catch (error) {
        console.error('Direct OAuth login error:', error);
        // Error notification is already shown in redirectToOAuthProvider
      }
    },
    [notification, t],
  );

  // Note: MSAL callback handling is disabled - Direct OAuth uses /oauth/callback route instead
  // The MSAL code below is kept for backward compatibility but should not be used with Direct OAuth

  /**
   * Generic handler for social login (Google or EntraID)
   */
  const handleSocialLogin = useCallback(
    async (type: SocialLoginTypeEnum): Promise<void> => {
      try {
        setIsRedirectingToOAuth(true);

        switch (type) {
          case SocialLoginTypeEnum.EntraID:
            if (!import.meta.env.VITE_APP_ENTRAID_CLIENT_ID) {
              notification.error({
                title: t('errors.configurationError'),
                description: 'EntraID client ID not configured',
              });
              setIsRedirectingToOAuth(false);
              return;
            }
            await msalInstance.loginRedirect(loginRequest);
            break;

          case SocialLoginTypeEnum.Google:
            if (!import.meta.env.VITE_APP_GOOGLE_CLIENT_ID) {
              notification.error({
                title: t('errors.configurationError'),
                description: 'Google client ID not configured',
              });
              setIsRedirectingToOAuth(false);
              return;
            }

            // Use Google Identity Services for OAuth
            if (window.google?.accounts?.oauth2) {
              const clientId = import.meta.env.VITE_APP_GOOGLE_CLIENT_ID;

              // Initialize Google OAuth
              const tokenClient = window.google.accounts.oauth2.initTokenClient({
                client_id: clientId,
                scope: 'openid email profile',
                callback: async (response: { access_token: string }) => {
                  if (response.access_token) {
                    // Send token to backend for authentication
                    // Example: await authService.authenticateWithGoogle(response.access_token);
                    // After backend authentication, call getUserInfo
                    // await getUserInfo(true);
                    setIsRedirectingToOAuth(false);
                  }
                },
              });
              tokenClient.requestAccessToken();
            } else {
              // Fallback to OAuth2 redirect if GIS is not available
              const clientId = import.meta.env.VITE_APP_GOOGLE_CLIENT_ID;
              const redirectUri = encodeURIComponent(
                import.meta.env.VITE_APP_GOOGLE_REDIRECT_URI || `${window.location.origin}/login`,
              );
              const scope = encodeURIComponent('openid email profile');
              const responseType = 'code';
              const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=${responseType}&scope=${scope}&access_type=offline&prompt=consent`;

              window.location.href = googleAuthUrl;
            }
            break;

          default:
            console.error('Unknown social login type:', type);
            setIsRedirectingToOAuth(false);
            break;
        }
      } catch (error: any) {
        if (error.status === 429) {
          notification.error({
            title: t('errors.tooManyRequests'),
          });
          setIsRedirectingToOAuth(false);
          return;
        }

        console.error('Social login error:', error);
        notification.error({
          title: t('errors.loginFailed'),
        });
        setIsRedirectingToOAuth(false);
      }
    },
    [msalInstance, notification, t],
  );

  // Note: Google OAuth callback is now handled by OAuthCallback component at /oauth/callback route
  // This useEffect is disabled for Direct OAuth flow

  /** Whether to show passwordless sign-in (browser support and no prior "not enabled" response). */
  const showPasswordlessLogin = useMemo(
    () => isPasskeySupported() && !passwordlessHiddenByPolicy,
    [passwordlessHiddenByPolicy],
  );

  return {
    form,
    handleRemoveFile,
    handleRequestOtp,
    isLoading,
    isLoadingOverlayVisible,
    isLoggedIn,
    isOtpRequested,
    onSubmit,
    otpTimer,
    selectedFile,
    setForm,
    isEmailValid,
    authenticate,
    isPageLoaded,
    otpInputRef,
    t,
    isSubmitBlocked,
    submitBlockTimer,
    otpResetKey,
    setOtpResetKey,
    isRedirectingToOAuth,
    setIsRedirectingToOAuth,
    handleSocialLogin,
    oauthProviders,
    handleDirectOAuthLogin,
    // Partial auth / second factor (TOTP and/or passkey after OTP when required)
    totpRequiredData,
    totpCode,
    setTotpCode,
    handleTotpCodeChange,
    totpInputRef,
    totpRemainingSeconds,
    autoOpenTotpSetupModal,
    clearAutoOpenTotpSetupModal,
    resetToEmailOnly,
    clearTotpStep,
    handleOpenTotpSetup,
    useBackupCodeMode,
    toggleTotpInputMode,
    chooseTwoFactorTotp,
    chooseTwoFactorPasskey,
    handlePasswordlessLogin,
    showPasswordlessLogin,
    a11yLiveMessage,
    // Mandatory TOTP setup (no second factor yet, grace period elapsed) after OTP
    totpSetupRequiredData,
    totpSetupRemainingSeconds,
    handleForcedTotpSetupSuccess,
    handleForcedTotpSetupExpired,
  };
}

export default useLogin;
